import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, ThermometerSun, Plane, Landmark, Loader2, MapPin } from "lucide-react";
import { useAuth } from "../auth";
import { useI18n } from "../i18n";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("sp_token")}` });

export default function MyWorld() {
  const { user } = useAuth();
  const { tr } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/myworld/feed`, { headers: authHeaders() });
      setData(res.data);
    } catch (_) { /* ignore */ } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("wp:follows-changed", h);
    return () => window.removeEventListener("wp:follows-changed", h);
  }, [load]);

  const unfollow = async (name) => {
    await axios.delete(`${API}/follows/${encodeURIComponent(name)}`, { headers: authHeaders() });
    toast.info(`${tr("Unfollowed")} ${name}`);
    window.dispatchEvent(new Event("wp:follows-changed"));
  };

  if (!user) return null;

  return (
    <section id="myworld" data-testid="myworld-section" className="py-14 lg:py-16 bg-white border-b wp-rule">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
        <Reveal>
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-2xl md:text-3xl font-extrabold text-[#0F2557] tracking-tight">{tr("My World")}</h2>
            <span className="text-[0.62rem] font-extrabold tracking-widest px-3 py-1 rounded-full bg-[#EAF2FD] text-[#1D4ED8]">
              {user.name?.split(" ")[0]}
            </span>
            {loading && <Loader2 size={16} className="animate-spin text-[#2563EB]" />}
            <a href="/digest" data-testid="digest-link"
              className="ml-auto text-xs font-extrabold text-[#7C3AED] bg-[#F5F3FF] border border-[#D8CCF2] rounded-full px-4 py-1.5 hover:bg-[#EDE7F8] transition-colors">
              {tr("Weekly Digest")} →
            </a>
          </div>
          <p className="text-[#64748B] text-sm mt-2">{tr("A personal feed from every territory you follow.")}</p>
        </Reveal>

        {data?.follows?.length ? (
          <>
            <div className="flex flex-wrap gap-2 mt-6" data-testid="followed-places-chips">
              {data.follows.map((f) => (
                <span key={f.place_name} className="inline-flex items-center gap-2 bg-[#F6FAFF] border border-[#BFD4F2] rounded-full pl-4 pr-2 py-1.5 text-sm font-bold text-[#0F2557]">
                  <MapPin size={13} className="text-[#2563EB]" /> {f.place_name}
                  <button data-testid={`unfollow-${f.place_name}`} onClick={() => unfollow(f.place_name)} className="p-1 rounded-full hover:bg-[#FEE2E2] hover:text-[#DC2626] text-[#94A3B8] transition-colors">
                    <X size={13} />
                  </button>
                </span>
              ))}
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-6" data-testid="myworld-feed-grid">
              {data.feed.map((c) => (
                <div key={c.country} className="border border-[#E5EDF7] rounded-3xl p-6 bg-[#F6FAFF] hover:shadow-lg hover:shadow-blue-100 transition-shadow duration-300">
                  <div className="flex items-center justify-between">
                    <span className="font-extrabold text-lg text-[#0F2557]">{c.flag} {c.country}</span>
                    {c.capital && <span className="text-xs text-[#64748B]">{c.capital}</span>}
                  </div>
                  {c.weather && (
                    <div className="flex items-center gap-3 mt-4 bg-white rounded-2xl border border-[#DCE8F7] px-4 py-3">
                      <ThermometerSun size={20} className="text-[#EA580C]" />
                      <div className="flex-1">
                        <div className="font-extrabold text-[#0F2557]">{Math.round(c.weather.temp)}°C · {c.weather.condition}</div>
                        <div className="text-[0.65rem] text-[#64748B]">H {Math.round(c.weather.high)}° / L {Math.round(c.weather.low)}° · {tr("wind")} {c.weather.wind} km/h</div>
                      </div>
                      <span className="text-[0.55rem] font-extrabold tracking-widest text-[#15803D] bg-[#F0FDF4] border border-[#C7E8D2] rounded-full px-2 py-0.5">LIVE</span>
                    </div>
                  )}
                  <div className="mt-3 space-y-2">
                    <div className="flex items-start gap-2.5 text-xs text-[#334155]">
                      <Landmark size={14} className="text-[#7C3AED] mt-0.5 shrink-0" />
                      <span><span className="font-bold">{c.civic.items[0].title}:</span> {c.civic.items[0].body} <em className="text-[#94A3B8] not-italic text-[0.6rem]">({tr("demo feed")})</em></span>
                    </div>
                    <div className="flex items-start gap-2.5 text-xs text-[#334155]">
                      <Plane size={14} className="text-[#2563EB] mt-0.5 shrink-0" />
                      <span className="font-mono-wp">{c.aviation.items[0].flight} {c.aviation.items[0].route} · {c.aviation.items[0].time} · {c.aviation.items[0].status} <em className="text-[#94A3B8] not-italic font-sans text-[0.6rem]">({tr("demo feed")})</em></span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          !loading && (
            <div data-testid="myworld-empty-state" className="mt-6 border border-dashed border-[#BFD4F2] rounded-3xl p-8 text-center">
              <p className="text-[#64748B] text-sm">{tr("You're not following any places yet. Tap a country on the world map and press Follow.")}</p>
              <button
                data-testid="myworld-goto-map"
                onClick={() => document.getElementById("worldmap")?.scrollIntoView({ behavior: "smooth" })}
                className="mt-4 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold text-sm px-6 py-2.5 rounded-xl transition-colors"
              >
                {tr("Open the world map")} →
              </button>
            </div>
          )
        )}
      </div>
    </section>
  );
}
